import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ClipboardPaste, FileCode2, Link2, Loader2, TriangleAlert, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { optimizeHtmlImages, byteSize, MAX_HTML_BYTES } from "@/lib/htmlOptimize";

const MODES = [
  { id: "paste", icon: ClipboardPaste, title: "Colar HTML", desc: "Cole o código gerado pela IA ou suba um arquivo .html" },
  { id: "url", icon: Link2, title: "Clonar URL", desc: "Importe uma página existente — abrimos num navegador real, então sites React/Next também funcionam" },
  { id: "blank", icon: FileCode2, title: "Em branco", desc: "Comece de um modelo simples" },
];

const fmtMB = (bytes) => (bytes / 1e6).toFixed(1);

export const NewPageDialog = ({ folderId, onClose }) => {
  const nav = useNavigate();
  const [mode, setMode] = useState("paste");
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [prepare, setPrepare] = useState(true);
  const [steps, setSteps] = useState(null);
  const [opt, setOpt] = useState(null); // { done, total } while optimizing

  // Re-encodes embedded base64 images to WebP; returns the (hopefully) smaller HTML.
  const optimize = async (raw) => {
    setOpt({ done: 0, total: 1 });
    const before = byteSize(raw);
    const { html: out, converted, total } = await optimizeHtmlImages(raw, (done, t) => setOpt({ done, total: t }));
    setOpt(null);
    const after = byteSize(out);
    if (converted > 0) toast.success(`${converted} de ${total} imagem(ns) convertida(s) para WebP · ${fmtMB(before)} MB → ${fmtMB(after)} MB`);
    else if (total > 0) toast.message("Nenhuma imagem pôde ser reduzida.");
    return out;
  };

  const file = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const text = await f.text();
    if (!name) setName(f.name.replace(/\.html?$/i, ""));
    if (byteSize(text) > MAX_HTML_BYTES) {
      toast.message(`Arquivo de ${fmtMB(f.size)} MB — otimizando imagens para caber no limite…`);
      const out = await optimize(text);
      setHtml(out);
      if (byteSize(out) > MAX_HTML_BYTES) toast.error(`Ainda ficou em ${fmtMB(byteSize(out))} MB (limite 10 MB). Remova algumas imagens pesadas.`);
    } else {
      setHtml(text);
    }
  };

  const create = async () => {
    let payload = html;
    if (mode === "paste" && byteSize(payload) > MAX_HTML_BYTES) {
      payload = await optimize(payload);
      setHtml(payload);
      if (byteSize(payload) > MAX_HTML_BYTES) return toast.error(`HTML ainda acima de 10 MB (${fmtMB(byteSize(payload))} MB) mesmo após otimizar.`);
    }
    setBusy(true);
    try {
      setSteps(prepare && mode === "url" ? { clone: "running", mirror: "wait", optimize: "wait" } : null);
      const { data } = await api.post("/projects", { name: name || "Nova página", mode, html: payload, url, folder_id: folderId || null });
      if (mode !== "url") toast.success("Página criada");
      else if (!prepare) toast.success(data.rendered ? "Página clonada com o navegador (JavaScript executado)" : "Página clonada do HTML bruto — se o site for React/Next, pode vir incompleta");
      if (prepare && mode === "url") await preparePage(data.id, data.rendered);
      nav(`/editor/${data.id}`);
    } catch (e) {
      setSteps(null);
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Clone → mirror → optimize, so the page already lands independent from the origin and light.
  const preparePage = async (pid, rendered) => {
    let mirrored = 0, savedPct = 0, independent = false;
    setSteps({ clone: "done", mirror: "running", optimize: "wait" });
    try {
      let last = null, rounds = 0;
      do {
        const { data } = await api.post(`/projects/${pid}/mirror`, { apply: true });
        mirrored += data.mirrored;
        independent = data.independent;
        const progressed = last === null || data.remaining < last;
        last = data.remaining;
        rounds++;
        if (!data.remaining || !progressed || rounds >= 5) break;
      } while (true);
      setSteps({ clone: "done", mirror: "done", optimize: "running" });
    } catch {
      setSteps({ clone: "done", mirror: "fail", optimize: "running" });
    }
    try {
      const { data } = await api.post(`/projects/${pid}/optimize`, { apply: true });
      savedPct = data.saved_pct;
      setSteps((s) => ({ ...s, optimize: "done" }));
    } catch {
      setSteps((s) => ({ ...s, optimize: "fail" }));
    }
    toast.success(`${rendered ? "Clonada com navegador" : "Clonada do HTML bruto"} · ${mirrored} arquivo(s) espelhado(s)${independent ? " (livre da origem)" : ""}${savedPct ? ` · imagens −${savedPct}%` : ""}`);
  };
  const heavy = html.length > 2_000_000;
  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => !busy && !opt && onClose()}>
      <div data-testid="new-page-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between">
          <div><h2 className="font-display font-bold text-2xl text-[var(--ink)]">Nova página</h2><p className="text-sm text-slate-500 mt-1">Escolha como você quer começar.</p></div>
          <button onClick={onClose} data-testid="new-page-close" className="h-9 w-9 rounded-full hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {MODES.map((m) => (
            <button key={m.id} data-testid={`new-page-mode-${m.id}`} onClick={() => setMode(m.id)} className={`text-left rounded-2xl border p-4 transition-colors ${mode === m.id ? "border-[var(--blue)] bg-[var(--blue)]/5" : "border-slate-200 hover:border-slate-300"}`}>
              <m.icon size={18} className={mode === m.id ? "text-[var(--blue)]" : "text-slate-500"} />
              <div className="mt-3 font-semibold text-sm text-[var(--ink)]">{m.title}</div>
              <div className="text-xs text-slate-500 mt-1 leading-snug">{m.desc}</div>
            </button>
          ))}
        </div>
        <label className="block mt-6"><span className="text-sm font-medium text-slate-700">Nome da página</span>
          <input data-testid="new-page-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Oferta de setembro" className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-[var(--blue)]" /></label>
        {mode === "paste" && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-medium text-slate-700">HTML</span>
              <label className="text-xs text-[var(--blue)] font-medium cursor-pointer flex items-center gap-1"><Upload size={12} /> subir arquivo .html<input type="file" accept=".html,.htm" className="hidden" onChange={file} data-testid="new-page-file" /></label></div>
            <textarea data-testid="new-page-html" value={html} onChange={(e) => setHtml(e.target.value)} rows={8} placeholder="<!DOCTYPE html> …" className="w-full rounded-xl border border-slate-200 p-3 font-mono text-xs outline-none focus:border-[var(--blue)]" />
            {opt && (
              <div data-testid="new-page-optimizing" className="mt-2 flex items-center gap-2 text-xs text-[var(--blue)]">
                <Loader2 size={13} className="animate-spin" /> Convertendo imagens para WebP… {opt.done}/{opt.total}
                <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-[var(--blue)] transition-all" style={{ width: `${Math.round((opt.done / Math.max(opt.total, 1)) * 100)}%` }} /></span>
              </div>
            )}
            {!opt && heavy && <p className="mt-1 text-xs text-amber-600">Esse HTML está pesado ({(html.length / 1e6).toFixed(1)} MB). Acima de 10 MB, as imagens em base64 são convertidas para WebP automaticamente ao subir/criar.</p>}
          </div>
        )}
        {mode === "url" && <label className="block mt-4"><span className="text-sm font-medium text-slate-700">URL da página</span>
          <input data-testid="new-page-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://exemplo.com/oferta" className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-[var(--blue)]" />
          <span data-testid="new-page-url-hint" className="mt-1.5 block text-xs text-slate-500">Abrimos a página num navegador real e esperamos o JavaScript renderizar — sites com verificação anti-bot podem levar até 1 minuto.</span></label>}

        {mode === "url" && (
          <div className="mt-4 rounded-2xl border border-slate-200 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="new-page-prepare" checked={prepare} onChange={(e) => setPrepare(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--blue)]" />
              <span>
                <span className="block text-sm font-medium text-[var(--ink)]">Deixar a página pronta</span>
                <span className="block text-xs text-slate-500 mt-0.5 leading-snug">Depois de clonar, espelhamos o CSS, as fontes e as imagens do site de origem e convertemos tudo para WebP — a página chega independente e leve. Leva mais um ou dois minutos.</span>
              </span>
            </label>
            {steps && (
              <div data-testid="new-page-steps" className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                {[["clone", "Clonando no navegador"], ["mirror", "Espelhando CSS, fontes e imagens"], ["optimize", "Convertendo imagens para WebP"]].map(([k, label]) => (
                  <div key={k} data-testid={`new-page-step-${k}`} className={`flex items-center gap-2 text-xs ${steps[k] === "wait" ? "text-slate-400" : steps[k] === "fail" ? "text-amber-600" : steps[k] === "done" ? "text-emerald-600" : "text-[var(--blue)]"}`}>
                    {steps[k] === "running" ? <Loader2 size={13} className="animate-spin" /> : steps[k] === "done" ? <Check size={13} /> : steps[k] === "fail" ? <TriangleAlert size={13} /> : <span className="h-[13px] w-[13px] rounded-full border border-current opacity-40" />}
                    {label}{steps[k] === "fail" ? " — pulado, você pode rodar no editor" : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-ghost">Cancelar</button>
          <button data-testid="new-page-submit" disabled={busy || !!opt} onClick={create} className="btn btn-primary disabled:opacity-60">{opt ? "Otimizando…" : busy ? (mode === "url" ? (steps ? "Preparando…" : "Clonando…") : "Criando…") : mode === "url" && prepare ? "Clonar e preparar" : "Criar página"}</button>
        </div>
      </div>
    </div>
  );
};
